<script setup lang="ts" generic="T extends { uuid: string }">
import { computed, ref, watch } from "vue"

const props = withDefaults(
  defineProps<{
    items: T[]
    loading: boolean
    hasMore: boolean
    selectedIds: Set<string>
    query: string
    labelFn: (item: T) => string
    placeholder?: string
    emptyLabel?: string
    /** Offer a "create <query>" row when the query matches no item exactly. */
    creatable?: boolean
    createLabelFn?: (query: string) => string
  }>(),
  {
    placeholder: "Suchen...",
    emptyLabel: "Keine Treffer",
    creatable: false,
    createLabelFn: (query: string) => `„${query}“ anlegen`
  }
)

const emit = defineEmits<{
  "update:query": [string]
  "select": [T]
  "load-more": []
  "close": []
  "submit-empty": []
  "create": [string]
}>()

const highlightedIndex = ref(-1)
const inputEl = ref<HTMLInputElement | null>(null)

defineExpose({
  focusInput: () => inputEl.value?.focus()
})

const filteredItems = computed<T[]>(() =>
  props.items.filter((item) => !props.selectedIds.has(item.uuid))
)

watch(filteredItems, () => { highlightedIndex.value = -1 })

const showCreateRow = computed<boolean>(() => {
  if (!props.creatable || props.loading) return false
  const q = props.query.trim()
  if (q === "") return false
  const lower = q.toLowerCase()
  return !props.items.some((item) => props.labelFn(item).trim().toLowerCase() === lower)
})

function onCreate(): void {
  emit("create", props.query.trim())
}

function onQueryInput(e: Event): void {
  const value = (e.target as HTMLInputElement).value
  emit("update:query", value)
  highlightedIndex.value = -1
}

function onKeydown(e: KeyboardEvent): void {
  const total = filteredItems.value.length

  if (e.key === "Backspace" && props.query === "") {
    emit("submit-empty")
    return
  }
  if (e.key === "ArrowDown") {
    e.preventDefault()
    if (total === 0) { highlightedIndex.value = -1; return }
    highlightedIndex.value = highlightedIndex.value < 0 ? 0 : (highlightedIndex.value + 1) % total
  } else if (e.key === "ArrowUp") {
    e.preventDefault()
    if (total === 0) { highlightedIndex.value = -1; return }
    highlightedIndex.value = highlightedIndex.value <= 0 ? total - 1 : highlightedIndex.value - 1
  } else if (e.key === "Enter") {
    const item = filteredItems.value[highlightedIndex.value]
    if (item) {
      e.preventDefault()
      emit("select", item)
    } else if (showCreateRow.value) {
      e.preventDefault()
      onCreate()
    }
  } else if (e.key === "Escape") {
    e.preventDefault()
    emit("close")
  }
}

function onSelect(item: T): void {
  emit("select", item)
}

// Emits can fire multiple times on rapid scroll before the parent flips :loading.
// The final de-duplication guard lives in useAsyncEntitySearch.loadMore() (sequence
// counter + loading check). Do not add a throttle here — it would only mask the
// composable's own guarantees.
function onScroll(e: Event): void {
  if (!props.hasMore || props.loading) return
  const el = e.target as HTMLElement
  const scrollBottom = el.scrollHeight - el.scrollTop - el.clientHeight
  if (scrollBottom < 60) emit("load-more")
}
</script>

<template>
  <div class="flex flex-col h-full min-h-0">
    <div class="p-2 border-b border-base-300 shrink-0">
      <input
        ref="inputEl"
        data-testid="combobox-input"
        class="input input-sm input-bordered w-full"
        type="text"
        :value="query"
        :placeholder="placeholder"
        role="combobox"
        aria-expanded="true"
        aria-autocomplete="list"
        @input="onQueryInput"
        @keydown="onKeydown"
      >
    </div>

    <div
      data-testid="combobox-content"
      role="listbox"
      class="overflow-auto flex-1 max-h-80 min-h-0"
      @scroll="onScroll"
    >
      <div
        v-if="loading && filteredItems.length === 0"
        data-testid="combobox-loading-initial"
        class="flex items-center justify-center gap-2 p-4 text-sm text-base-content/60"
      >
        <span class="loading loading-spinner loading-md" />
        <span>Lädt...</span>
      </div>
      <div
        v-else-if="!loading && filteredItems.length === 0 && !showCreateRow"
        class="p-3 text-sm text-base-content/60"
      >
        {{ emptyLabel }}
      </div>
      <div v-else-if="filteredItems.length > 0 || showCreateRow" role="presentation">
        <button
          v-for="(item, idx) in filteredItems"
          :key="item.uuid"
          :data-testid="`combobox-item-${item.uuid}`"
          type="button"
          role="option"
          aria-selected="false"
          class="w-full flex items-start gap-2 px-3 py-2 hover:bg-base-200 text-left"
          :class="{ 'bg-primary/10': idx === highlightedIndex }"
          @mousedown.prevent="onSelect(item)"
          @mouseenter="highlightedIndex = idx"
        >
          <slot name="option" :item="item">
            <span class="flex-1">{{ labelFn(item) }}</span>
          </slot>
        </button>
        <button
          v-if="showCreateRow"
          data-testid="combobox-create-row"
          type="button"
          role="option"
          aria-selected="false"
          class="w-full flex items-center gap-2 px-3 py-2 hover:bg-base-200 text-left text-primary border-t border-base-300"
          @mousedown.prevent="onCreate"
        >
          <span aria-hidden="true">+</span>
          <span class="flex-1">{{ createLabelFn(query.trim()) }}</span>
        </button>
        <div
          v-if="loading"
          data-testid="combobox-loading-more"
          class="flex items-center justify-center gap-2 p-3 text-sm text-base-content/60"
        >
          <span class="loading loading-dots loading-sm" />
          <span>Lade weitere...</span>
        </div>
      </div>
      <div
        v-if="!loading && filteredItems.length > 0"
        data-testid="combobox-footer"
        class="p-2 text-xs text-base-content/50 border-t border-base-300"
      >
        <template v-if="hasMore">Für mehr Treffer weiter scrollen</template>
        <template v-else>Alle Treffer angezeigt</template>
      </div>
    </div>
  </div>
</template>
